const ItemType = require('@/lib/Items/ItemType');
const Order = require('@/lib/Orders/Order');

/**
 * This object holds an array of Orders loaded from the API for a search.
 *
 * The page numbers need special attention, because each request to the API returns 50
 * results per page. But on ProPoint we display 10 results per page.
 */
class OrderSearchResults {
    /**
     * How many orders does ProPoint display per page.
     *
     * @return {number}
     */
    static getOrdersPerProPointPage() {
        return 10;
    }

    /**
     * @param {boolean} wasOnlineSearch
     */
    constructor(wasOnlineSearch = true) {
        /**
         * @type {Order[]}
         */
        this.orders = [];

        /**
         * Is this a search that was performed online (true) (getting fresh results from the API)?
         * Or offline (false) (getting results from the offline order cache)?
         *
         * @type {boolean}
         */
        this.wasOnlineSearch = wasOnlineSearch;

        /**
         * @type {number}
         */
        this.ordersPerApiPage = 50;

        /**
         * How many pages of results (in ProPoint page size chunks) are currently loaded.
         *
         * @type {number}
         */
        this.proPointPagesCurrentlyLoaded = 0;

        /**
         * How many pages of results (in API page size chunks) are currently loaded.
         *
         * @type {number}
         */
        this.apiPagesCurrentlyLoaded = 0;

        /**
         * A count of the number of times `appendApiResponse` has been called.
         *
         * @type {number}
         */
        this.apiResponsesAppended = 0;

        /**
         * This is disabled because the API does not currently return this information.
         *
         * @type {number}
         */
        // this.totalPages = Infinity;

        /**
         * Are these results for a specific search query (true), or just for hitting the button to show all recent
         * orders (false)?
         *
         * @type {boolean}
         */
        this.wasCustomSearch = false;

        /**
         * The parameters used in the last API query to load these results.
         *
         * @type {{}}
         */
        this.searchQueryParams = {};

        /**
         * @type {string|null}
         */
        this.nextApiPageUrl = null;

        /**
         * @type {boolean}
         */
        this.requestIsPending = false;
    }

    setSearchQueryParams(queryParams) {
        this.searchQueryParams = queryParams;

        // Determine if this was a custom search.
        this.wasCustomSearch = Object.keys(queryParams).length > 0;
    }

    /**
     * @param {Order[]} orders
     */
    appendOrders(orders) {
        orders
            .filter((order) => {
                // Skip any orders that contain nothing but 'Generic' items.
                // But keep those that include zero lines.
                return !!order.items.find((item) => {
                    return item.itemType !== ItemType.GENERIC;
                }) || order.items.length === 0;
            })
            .forEach((order) => {
                this.orders.push(order);
            });

        this.proPointPagesCurrentlyLoaded = Math.ceil(
            this.orders.length / OrderSearchResults.getOrdersPerProPointPage()
        );
        this.apiPagesCurrentlyLoaded = Math.ceil(
            this.orders.length / this.ordersPerApiPage
        );
    }

    /**
     * @param {{data: any, links: {}}} res
     * @param {Hydrator} hydrator
     */
    appendApiResponse(res, hydrator) {
        this.requestIsPending = false;

        // Add orders from the response body
        if (res.data) {
            const orders = hydrator.hydrateArray(res.data, () => new Order());
            this.appendOrders(orders);
        }

        // Set the next page URL.
        this.nextApiPageUrl = res.links && res.links.next ? res.links.next.url : null;

        this.apiResponsesAppended += 1;
    }
}

module.exports = OrderSearchResults;
